import { useState } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  getSupportedLocales,
  saveLocale,
  loadLocaleAsync,
} from '@/lib/locale-utils';
import type { Locales } from '@/i18n/i18n-types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { twMerge } from 'tailwind-merge';

/** Loads, switches and saves the locale (used by this selector and the Mais). */
export function useLocaleSwitcher(): {
  locale: Locales;
  isChanging: boolean;
  changeLocale: (locale: Locales) => Promise<void>;
} {
  const { locale, setLocale } = useI18nContext();
  const [isChanging, setIsChanging] = useState(false);

  const changeLocale = async (newLocale: Locales) => {
    if (newLocale === locale) return;

    setIsChanging(true);
    try {
      await loadLocaleAsync(newLocale);
      setLocale(newLocale);
      saveLocale(newLocale);
    } catch (error) {
      console.error('Failed to load locale:', error);
    } finally {
      setIsChanging(false);
    }
  };

  return { locale, isChanging, changeLocale };
}

export function LanguageSelector({ className }: { className?: string }) {
  const { locale, isChanging, changeLocale } = useLocaleSwitcher();
  const supportedLocales = getSupportedLocales();

  const currentLocale = supportedLocales.find((l) => l.code === locale);

  return (
    <Select value={locale} onValueChange={changeLocale} disabled={isChanging}>
      <SelectTrigger className={twMerge('w-full', className)}>
        <SelectValue>
          <span className={'flex items-center gap-2'}>
            <span>{currentLocale?.flag}</span>
            <span className="hidden sm:inline">{currentLocale?.name}</span>
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {supportedLocales.map((locale) => (
          <SelectItem key={locale.code} value={locale.code}>
            <span className="flex items-center gap-2">
              <span>{locale.flag}</span>
              <span>{locale.name}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
